import { getByPath, parse, type Expression } from '@de/utils/interpolation';

/**
 * Optional facet query params (`language={{formState.language}}`) start
 * empty. `hasUnresolvedDependency` used to skip the whole fetch; now it
 * lets query-only formState through and this helper drops those keys so
 * the request is unfiltered until a Select is picked.
 *
 * Only omit when every path token (including across array items) is
 * `formState.*` and at least one is empty. Leave `context.*`,
 * sibling-source names, and static values alone.
 */
export function omitEmptyFormStateQueryKeys<
  T extends Readonly<Record<string, string | readonly string[]>>,
>(query: T, context: unknown): T {
  let omitted = false;
  const next: Record<string, string | readonly string[]> = {};
  for (const [key, value] of Object.entries(query)) {
    if (shouldOmitEmptyFormStateQueryValue(value, context)) {
      omitted = true;
      continue;
    }
    next[key] = value;
  }
  return (omitted ? next : query) as T;
}

export function shouldOmitEmptyFormStateQueryValue(
  value: unknown,
  context: unknown,
): boolean {
  const expressions = collectPathExpressions(value);
  if (expressions === null || expressions.length === 0) return false;

  const allFormState = expressions.every((expression) => {
    const root = expression.segments[0];
    return root?.type === 'key' && root.name === 'formState';
  });
  if (!allFormState) return false;

  return expressions.some((expression) =>
    isEmptyResolved(getByPath(context, expression.segments)),
  );
}

function collectPathExpressions(value: unknown): readonly Expression[] | null {
  if (typeof value === 'string') {
    return collectPathExpressionsFromString(value);
  }
  if (Array.isArray(value)) {
    const all: Expression[] = [];
    for (const item of value) {
      const next = collectPathExpressions(item);
      if (next === null) return null;
      all.push(...next);
    }
    return all;
  }
  return [];
}

function collectPathExpressionsFromString(value: string) {
  let segments;
  try {
    ({ segments } = parse(value));
  } catch {
    return null;
  }

  const expressions = segments.filter(
    (segment) => segment.type === 'expression',
  );
  if (expressions.some((expression) => expression.kind !== 'path')) {
    return null;
  }
  return expressions;
}

function isEmptyResolved(value: unknown): boolean {
  return (
    value == null ||
    (typeof value === 'string' && value.trim().length === 0) ||
    // A cleared multi-select writes `[]`; omit the optional facet the same
    // way a blank string is omitted so `regions=` is never sent empty.
    (Array.isArray(value) && value.length === 0)
  );
}
